import React, { useState, useRef, useEffect } from 'react';
import { Check, X, Pencil } from 'lucide-react';

interface InlineEditFieldProps {
  value: string | number;
  onSave: (value: string | number) => Promise<void>;
  type?: 'text' | 'number' | 'email' | 'tel';
  placeholder?: string;
  disabled?: boolean;
  suffix?: string;
  className?: string;
  displayValue?: string;
}

export default function InlineEditField({
  value,
  onSave,
  type = 'text',
  placeholder = '',
  disabled = false,
  suffix,
  className = '',
  displayValue,
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(String(value));
  }, [value]);

  const handleSave = async () => {
    if (isSaving) return;

    const newValue = type === 'number' ? parseInt(editValue) || 0 : editValue;
    if (newValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(newValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
      setEditValue(String(value));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(String(value));
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (disabled) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-slate-800">
          {displayValue || value || <span className="text-slate-400 italic">-</span>}
        </span>
        {suffix && <span className="text-slate-500">{suffix}</span>}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <input
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-white border border-blue-500 rounded px-3 py-1.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={placeholder}
          disabled={isSaving}
        />
        {suffix && <span className="text-slate-500">{suffix}</span>}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
          title="Speichern"
        >
          <Check size={18} />
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
          title="Abbrechen"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-2 py-1.5 -mx-2 transition-colors ${className}`}
      onClick={() => setIsEditing(true)}
    >
      <span className="text-slate-800">
        {displayValue || value || <span className="text-slate-400 italic">-</span>}
      </span>
      {suffix && <span className="text-slate-500">{suffix}</span>}
      <Pencil size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

interface InlineSelectFieldProps {
  value: string;
  options: { value: string; label: string }[];
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function InlineSelectField({
  value,
  options,
  onSave,
  placeholder = '-- Auswählen --',
  disabled = false,
  className = '',
}: InlineSelectFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = async () => {
    if (isSaving) return;

    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
      setEditValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const displayLabel = options.find(o => o.value === value)?.label || placeholder;

  if (disabled) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-slate-800">{displayLabel}</span>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <select
          ref={selectRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="flex-1 bg-white border border-blue-500 rounded px-3 py-1.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isSaving}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
          title="Speichern"
        >
          <Check size={18} />
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
          title="Abbrechen"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-2 py-1.5 -mx-2 transition-colors ${className}`}
      onClick={() => setIsEditing(true)}
    >
      <span className={value ? 'text-slate-800' : 'text-slate-400 italic'}>
        {displayLabel}
      </span>
      <Pencil size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}
